import React, { useState } from 'react';
import { useChatTheme, useThemeStyles } from '../../contexts/ChatThemeContext';
import { format } from 'date-fns';

interface MessageBubbleProps {
  message: {
    id: string;
    content: string;
    timestamp: Date;
    sender: 'user' | 'other';
    senderName?: string;
    status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  };
  showSenderName?: boolean;
  onReply?: () => void;
  onReact?: (emoji: string) => void;
  reactions?: Array<{ emoji: string; count: number; hasReacted: boolean }>;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  showSenderName = false,
  onReply,
  onReact,
  reactions = []
}) => {
  const { theme } = useChatTheme();
  const styles = useThemeStyles();
  const [isHovered, setIsHovered] = useState(false);
  
  const isSent = message.sender === 'user';
  const bubbleStyle = isSent ? styles.sentBubble : styles.receivedBubble;
  const hoverStyle = isSent ? styles.sentBubbleHover : styles.receivedBubbleHover;

  const formatTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  return (
    <div 
      className={`message-wrapper ${isSent ? 'sent' : ''}`}
      style={{
        display: 'flex',
        marginBottom: theme.spacing.messageGap,
        justifyContent: isSent ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        className="message-bubble"
        style={{
          ...bubbleStyle,
          ...(isHovered ? hoverStyle : {}),
          cursor: 'pointer',
          position: 'relative',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {showSenderName && message.senderName && !isSent && (
          <div style={{
            fontSize: '12px',
            color: theme.colors.accent,
            marginBottom: '4px',
            fontWeight: 600,
          }}>
            {message.senderName}
          </div>
        )}
        
        <div style={styles.messageText}>
          {message.content}
        </div>
        
        <div style={{
          ...styles.timeText,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: isSent ? 'flex-end' : 'flex-start',
        }}>
          <span>{formatTime(message.timestamp)}</span>
          
          {isSent && message.status && (
            <span style={{ fontSize: '10px' }}>
              {message.status === 'sending' && '⏳'}
              {message.status === 'sent' && '✓'}
              {message.status === 'delivered' && '✓✓'}
              {message.status === 'read' && <span style={{ color: theme.colors.accent }}>✓✓</span>}
              {message.status === 'failed' && '❌'}
            </span>
          )}
        </div>

        {/* Reactions */}
        {reactions.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '-12px',
            right: isSent ? '10px' : 'auto',
            left: isSent ? 'auto' : '10px',
            display: 'flex',
            gap: '4px',
          }}>
            {reactions.map((reaction, index) => (
              <button
                key={index}
                onClick={() => onReact?.(reaction.emoji)}
                style={{
                  background: reaction.hasReacted 
                    ? theme.colors.accent 
                    : theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '2px 6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  color: reaction.hasReacted ? 'white' : theme.colors.primaryText,
                }}
              >
                <span>{reaction.emoji}</span>
                <span>{reaction.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Quick actions on hover */}
        {isHovered && (
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: isSent ? '0' : 'auto',
            left: isSent ? 'auto' : '0',
            display: 'flex',
            gap: '4px',
            background: theme.name === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: '16px',
            padding: '4px',
            boxShadow: theme.shadows.receivedBubble,
          }}>
            <button
              onClick={onReply}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px',
                color: theme.colors.mutedText,
                borderRadius: '12px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.name === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              Reply
            </button>
            <button
              onClick={() => onReact?.('👍')}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '16px',
                borderRadius: '12px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.name === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              👍
            </button>
          </div>
        )}
      </div>
    </div>
  );
};